(() => {
  'use strict';

  const normalizeLocalizationSearch = (value) => String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLocaleLowerCase();

  const filterLocalizationCountries = (countries, query) => {
    const normalizedQuery = normalizeLocalizationSearch(query);
    if (!normalizedQuery) return [...countries];
    return countries.filter((country) => normalizeLocalizationSearch(country.searchValue).includes(normalizedQuery));
  };

  const resolveLocalizationCountry = (countries, countryCode) => (
    countries.find((country) => country.code === countryCode) || null
  );

  const submitLocalizationCountry = (nativeSelect, form, countries, countryCode) => {
    const country = resolveLocalizationCountry(countries, countryCode);
    if (!country || !nativeSelect || !form) return false;
    nativeSelect.value = country.code;
    if (nativeSelect.value !== country.code) return false;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else if (typeof form.submit === 'function') form.submit();
    else return false;
    return true;
  };

  if (typeof document === 'undefined' || typeof window === 'undefined') {
    if (typeof module !== 'undefined') {
      module.exports = {
        normalizeLocalizationSearch,
        filterLocalizationCountries,
        resolveLocalizationCountry,
        submitLocalizationCountry
      };
    }
    return;
  }

  const body = document.body;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const storageKeys = {
    wishlist: 'steezlab:wishlist'
  };

  const getFocusable = (container) => Array.from(container.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), details > summary, [tabindex]:not([tabindex="-1"])'
  )).filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);

  const safeStorage = {
    get(key, fallback = null) {
      try {
        const value = window.localStorage.getItem(key);
        return value === null ? fallback : JSON.parse(value);
      } catch (_error) {
        return fallback;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
        return true;
      } catch (_error) {
        return false;
      }
    },
    remove(key) {
      try {
        window.localStorage.removeItem(key);
      } catch (_error) {
        /* Storage is an enhancement only. */
      }
    }
  };

  const emit = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(`steezlab:${name}`, { detail }));
  };

  class FocusTrap {
    constructor(container, returnTarget) {
      this.container = container;
      this.returnTarget = returnTarget;
      this.onKeydown = this.onKeydown.bind(this);
    }

    activate() {
      document.addEventListener('keydown', this.onKeydown);
      const focusable = getFocusable(this.container);
      (focusable[0] || this.container).focus({ preventScroll: true });
    }

    deactivate() {
      document.removeEventListener('keydown', this.onKeydown);
      if (this.returnTarget && document.contains(this.returnTarget)) {
        this.returnTarget.focus({ preventScroll: true });
      }
    }

    onKeydown(event) {
      if (event.key !== 'Tab') return;
      const focusable = getFocusable(this.container);
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const overlays = new Map();
  const localizationInstances = new Map();

  function openOverlay(target, trigger) {
    if (!target || overlays.has(target)) return;
    const dialog = target.querySelector('[role="dialog"]') || target;
    target.hidden = false;
    target.classList.add('is-open');
    target.setAttribute('aria-hidden', 'false');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    body.classList.add('is-locked');
    const trap = new FocusTrap(dialog, trigger);
    overlays.set(target, { trigger, trap });
    requestAnimationFrame(() => trap.activate());
    emit('overlay-open', { id: target.id });
  }

  function closeOverlay(target, restoreFocus = true) {
    const state = overlays.get(target);
    if (!target || (!state && target.getAttribute('aria-hidden') !== 'false')) return;
    target.classList.remove('is-open');
    target.setAttribute('aria-hidden', 'true');
    target.querySelectorAll('[data-predictive-search-input]').forEach((input) => input.setAttribute('aria-expanded', 'false'));
    if (state?.trigger) state.trigger.setAttribute('aria-expanded', 'false');
    state?.trap.deactivate();
    if (!restoreFocus && state?.returnTarget) state.returnTarget = null;
    overlays.delete(target);
    if (!overlays.size) body.classList.remove('is-locked');
    window.setTimeout(() => {
      if (target.getAttribute('aria-hidden') === 'true') target.hidden = true;
    }, reducedMotion.matches ? 0 : 320);
    emit('overlay-close', { id: target.id });
  }

  function resolveTarget(control, fallbackSelector) {
    const id = control.getAttribute('aria-controls') || control.dataset.target;
    if (id) return document.getElementById(id.replace(/^#/, '')) || document.querySelector(id.startsWith('#') ? id : `#${CSS.escape(id)}`);
    return fallbackSelector ? document.querySelector(fallbackSelector) : null;
  }

  function initOverlays(scope = document) {
    scope.querySelectorAll('[data-menu-toggle], [data-mobile-menu-toggle], [data-mobile-menu-open], [data-menu-drawer-open]').forEach((control) => {
      if (control.dataset.slReady) return;
      control.dataset.slReady = 'true';
      control.addEventListener('click', (event) => {
        const target = resolveTarget(control, '[data-mobile-navigation], [data-mobile-menu], .mobile-menu, .menu-drawer');
        if (!target) return;
        event.preventDefault();
        target.getAttribute('aria-hidden') === 'false' ? closeOverlay(target) : openOverlay(target, control);
      });
    });

    scope.querySelectorAll('[data-search-toggle], [data-search-open]').forEach((control) => {
      if (control.dataset.slReady) return;
      control.dataset.slReady = 'true';
      control.addEventListener('click', (event) => {
        const target = resolveTarget(control, '[data-header-search], [data-search-modal], .search-modal');
        if (!target) return;
        event.preventDefault();
        openOverlay(target, control);
      });
    });

    scope.querySelectorAll('[data-overlay-close], [data-menu-close], [data-mobile-menu-close], [data-search-close]').forEach((control) => {
      if (control.dataset.slReady) return;
      control.dataset.slReady = 'true';
      control.addEventListener('click', () => {
        const target = control.closest('[data-mobile-navigation], [data-header-search], [aria-hidden], [role="dialog"], .mobile-menu, .menu-drawer, .search-modal');
        if (target) closeOverlay(target);
      });
    });

    scope.querySelectorAll('[data-overlay], [data-mobile-navigation], [data-header-search]').forEach((overlay) => {
      if (overlay.dataset.slOverlayReady) return;
      overlay.dataset.slOverlayReady = 'true';
      overlay.addEventListener('mousedown', (event) => {
        if (event.target === overlay) closeOverlay(overlay);
      });
    });
  }

  function initReveals(scope = document) {
    const elements = scope.querySelectorAll('[data-reveal]:not(.is-visible)');
    if (!elements.length) return;
    if (reducedMotion.matches || !('IntersectionObserver' in window) || window.Shopify?.designMode) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries, currentObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        currentObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -7% 0px', threshold: 0.08 });
    elements.forEach((element) => observer.observe(element));
  }

  function initDetails(scope = document) {
    scope.querySelectorAll('details[data-close-others]').forEach((details) => {
      if (details.dataset.slReady) return;
      details.dataset.slReady = 'true';
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        const group = details.dataset.closeOthers;
        document.querySelectorAll(`details[data-close-others="${CSS.escape(group)}"]`).forEach((other) => {
          if (other !== details) other.open = false;
        });
      });
    });
  }

  function initAnnouncements(scope = document) {
    scope.querySelectorAll('[data-announcement-bar]').forEach((bar) => {
      if (bar.dataset.slReady) return;
      const items = Array.from(bar.querySelectorAll('[data-announcement-item]'));
      if (items.length < 2) return;
      bar.dataset.slReady = 'true';
      const previous = bar.querySelector('[data-announcement-previous]');
      const next = bar.querySelector('[data-announcement-next]');
      const intervalSeconds = Number(bar.dataset.interval || bar.dataset.autoplayInterval || 5);
      const autoRotate = bar.dataset.autoRotate !== 'false' && bar.dataset.autoplay !== 'false';
      let current = Math.max(0, items.findIndex((item) => !item.hidden && item.getAttribute('aria-hidden') !== 'true'));
      let timer;

      const show = (index, announce = false) => {
        current = (index + items.length) % items.length;
        items.forEach((item, itemIndex) => {
          const active = itemIndex === current;
          item.hidden = !active;
          item.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
        if (announce) {
          const status = bar.querySelector('[data-announcement-status]');
          if (status) {
            status.textContent = '';
            window.requestAnimationFrame(() => { status.textContent = items[current].textContent.trim(); });
          }
        }
      };

      const pause = () => window.clearInterval(timer);
      const play = () => {
        pause();
        if (!autoRotate || reducedMotion.matches || document.hidden) return;
        timer = window.setInterval(() => show(current + 1), Math.max(3, intervalSeconds) * 1000);
      };

      previous?.addEventListener('click', () => { show(current - 1, true); play(); });
      next?.addEventListener('click', () => { show(current + 1, true); play(); });
      bar.addEventListener('mouseenter', pause);
      bar.addEventListener('mouseleave', play);
      bar.addEventListener('focusin', pause);
      bar.addEventListener('focusout', play);
      document.addEventListener('visibilitychange', () => document.hidden ? pause() : play());
      reducedMotion.addEventListener?.('change', play);
      show(current);
      play();
    });
  }

  function initWishlist(scope = document) {
    const wishlist = new Set(safeStorage.get(storageKeys.wishlist, []));
    scope.querySelectorAll('[data-wishlist-button]').forEach((button) => {
      if (button.dataset.slReady) return;
      const key = button.dataset.productHandle || button.dataset.productId;
      if (!key) return;
      button.dataset.slReady = 'true';
      button.setAttribute('aria-pressed', wishlist.has(key) ? 'true' : 'false');
      button.addEventListener('click', (event) => {
        event.preventDefault();
        if (wishlist.has(key)) wishlist.delete(key); else wishlist.add(key);
        button.setAttribute('aria-pressed', wishlist.has(key) ? 'true' : 'false');
        safeStorage.set(storageKeys.wishlist, Array.from(wishlist));
        emit('wishlist-change', { product: key, saved: wishlist.has(key), count: wishlist.size });
      });
    });
  }

  function initLocalization(scope = document) {
    scope.querySelectorAll('[data-localization-select]').forEach((select) => {
      if (select.dataset.slReady) return;
      select.dataset.slReady = 'true';
      select.addEventListener('change', () => {
        if (typeof select.form?.requestSubmit === 'function') select.form.requestSubmit();
        else select.form?.submit();
      });
    });

    localizationInstances.forEach((instance, selector) => {
      if (!document.contains(selector)) {
        instance.destroy();
        localizationInstances.delete(selector);
      }
    });

    scope.querySelectorAll('[data-localization-country]').forEach((selector) => {
      if (localizationInstances.has(selector)) return;

      const form = selector.closest('form');
      const nativeSelect = selector.querySelector('[data-localization-country-native]');
      const trigger = selector.querySelector('[data-localization-country-trigger]');
      const panel = selector.querySelector('[data-localization-country-panel]');
      const dialog = selector.querySelector('[data-localization-country-dialog]');
      const search = selector.querySelector('[data-localization-country-search]');
      const list = selector.querySelector('[data-localization-country-list]');
      const empty = selector.querySelector('[data-localization-country-empty]');
      const status = selector.querySelector('[data-localization-country-status]');
      const optionElements = [...selector.querySelectorAll('[data-localization-country-option]')];
      if (!form || !nativeSelect || !trigger || !panel || !dialog || !search || !list || !optionElements.length) return;

      const controller = new AbortController();
      const { signal } = controller;
      const mobileQuery = window.matchMedia('(max-width: 749px)');
      const countries = optionElements.map((element) => ({
        element,
        code: element.dataset.countryCode || '',
        name: element.dataset.countryName || '',
        currency: element.dataset.countryCurrency || '',
        searchValue: element.dataset.countrySearch || element.textContent
      }));
      let isOpen = false;
      let focusTrap = null;

      const mountPanelForMode = () => {
        const target = mobileQuery.matches ? body : selector;
        if (panel.parentNode !== target) target.append(panel);
      };

      const visibleCountries = () => countries.filter((country) => !country.element.hidden);
      const selectedCountry = () => resolveLocalizationCountry(countries, nativeSelect.value) || countries[0];
      const setRovingTarget = (target) => {
        countries.forEach((country) => {
          country.element.tabIndex = country === target ? 0 : -1;
        });
      };
      const focusCountry = (position) => {
        const visible = visibleCountries();
        if (!visible.length) return;
        let target;
        if (position === 'first') target = visible[0];
        else if (position === 'last') target = visible[visible.length - 1];
        else if (position === 'selected') target = visible.find((country) => country.code === nativeSelect.value) || visible[0];
        else {
          const activeIndex = visible.findIndex((country) => country.element === document.activeElement);
          const offset = position === 'previous' ? -1 : 1;
          target = visible[(activeIndex + offset + visible.length) % visible.length];
        }
        setRovingTarget(target);
        target.element.focus({ preventScroll: true });
        target.element.scrollIntoView({ block: 'nearest' });
      };
      const filterCountries = () => {
        const matches = filterLocalizationCountries(countries, search.value);
        const matchingElements = new Set(matches.map((country) => country.element));
        countries.forEach((country) => {
          country.element.hidden = !matchingElements.has(country.element);
        });
        dialog.classList.toggle('is-empty', matches.length === 0);
        if (empty) empty.hidden = matches.length > 0;
        const rovingTarget = matches.find((country) => country.code === nativeSelect.value) || matches[0] || null;
        setRovingTarget(rovingTarget);
        if (status) status.textContent = `${matches.length} ${status.dataset.resultsLabel || ''}`.trim();
      };
      const syncModalMode = () => {
        if (!isOpen) return;
        mountPanelForMode();
        if (mobileQuery.matches) {
          dialog.setAttribute('aria-modal', 'true');
          body.classList.add('is-localization-open');
          if (!focusTrap) {
            focusTrap = new FocusTrap(dialog, trigger);
            focusTrap.activate();
            search.focus({ preventScroll: true });
          }
        } else {
          dialog.removeAttribute('aria-modal');
          body.classList.remove('is-localization-open');
          if (focusTrap) {
            focusTrap.returnTarget = null;
            focusTrap.deactivate();
            focusTrap = null;
            search.focus({ preventScroll: true });
          }
        }
      };
      const close = (restoreFocus = true) => {
        if (!isOpen) return;
        isOpen = false;
        selector.classList.remove('is-open');
        panel.hidden = true;
        panel.setAttribute('aria-hidden', 'true');
        trigger.setAttribute('aria-expanded', 'false');
        dialog.removeAttribute('aria-modal');
        if (focusTrap) {
          if (!restoreFocus) focusTrap.returnTarget = null;
          focusTrap.deactivate();
          focusTrap = null;
        } else if (restoreFocus && document.contains(trigger)) {
          trigger.focus({ preventScroll: true });
        }
        if (![...localizationInstances.values()].some((instance) => instance.isOpen())) {
          body.classList.remove('is-localization-open');
        }
        if (panel.parentNode !== selector) selector.append(panel);
      };
      const open = (focusTarget = 'search') => {
        if (isOpen) return;
        localizationInstances.forEach((instance, otherSelector) => {
          if (otherSelector !== selector) instance.close(false);
        });
        isOpen = true;
        search.value = '';
        filterCountries();
        mountPanelForMode();
        selector.classList.add('is-open');
        panel.hidden = false;
        panel.setAttribute('aria-hidden', 'false');
        trigger.setAttribute('aria-expanded', 'true');
        syncModalMode();
        window.requestAnimationFrame(() => {
          if (!isOpen) return;
          if (focusTarget === 'search') search.focus({ preventScroll: true });
          else focusCountry(focusTarget);
        });
      };
      const chooseCountry = (country) => {
        if (!country) return;
        countries.forEach((candidate) => {
          const selected = candidate === country;
          candidate.element.classList.toggle('is-selected', selected);
          candidate.element.setAttribute('aria-selected', selected ? 'true' : 'false');
          const selectedLabel = candidate.element.querySelector('[data-localization-country-selected-label]');
          if (selectedLabel) selectedLabel.hidden = !selected;
        });
        selector.querySelector('[data-localization-current-country]')?.replaceChildren(country.name);
        selector.querySelector('[data-localization-current-currency]')?.replaceChildren(country.currency);
        close(false);
        submitLocalizationCountry(nativeSelect, form, countries, country.code);
      };
      const handleDialogKeydown = (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          close();
          return;
        }

        const country = countries.find((candidate) => candidate.element === event.target.closest('[data-localization-country-option]'));
        if (country && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          chooseCountry(country);
          return;
        }

        if (event.target === search && event.key === 'Enter') {
          event.preventDefault();
          const firstMatch = visibleCountries()[0];
          if (firstMatch) chooseCountry(firstMatch);
          return;
        }

        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        if (event.target !== search && !country) return;
        event.preventDefault();
        if (event.key === 'Home') focusCountry('first');
        else if (event.key === 'End') focusCountry('last');
        else if (event.target === search) focusCountry(event.key === 'ArrowDown' ? 'first' : 'last');
        else focusCountry(event.key === 'ArrowDown' ? 'next' : 'previous');
      };

      trigger.addEventListener('click', () => isOpen ? close() : open(), { signal });
      trigger.addEventListener('keydown', (event) => {
        if (!['Enter', ' ', 'ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (isOpen && (event.key === 'Enter' || event.key === ' ')) {
          close();
          return;
        }
        if (event.key === 'ArrowUp' || event.key === 'End') open('last');
        else if (event.key === 'Home') open('first');
        else if (event.key === 'ArrowDown') open('selected');
        else open('search');
      }, { signal });
      selector.querySelectorAll('[data-localization-country-close]').forEach((button) => {
        button.addEventListener('click', () => close(), { signal });
      });
      search.addEventListener('input', filterCountries, { signal });
      dialog.addEventListener('keydown', handleDialogKeydown, { signal });
      countries.forEach((country) => {
        country.element.addEventListener('click', () => chooseCountry(country), { signal });
      });
      document.addEventListener('pointerdown', (event) => {
        if (isOpen && !selector.contains(event.target) && !panel.contains(event.target)) close();
      }, { signal });
      mobileQuery.addEventListener?.('change', syncModalMode, { signal });

      setRovingTarget(selectedCountry());
      selector.dataset.slReady = 'true';
      localizationInstances.set(selector, {
        close,
        isOpen: () => isOpen,
        destroy() {
          close(false);
          controller.abort();
          selector.removeAttribute('data-sl-ready');
        }
      });
    });
  }

  function destroyLocalization(scope) {
    localizationInstances.forEach((instance, selector) => {
      if (selector === scope || scope.contains(selector)) {
        instance.destroy();
        localizationInstances.delete(selector);
      }
    });
  }

  function initQuantityControls(scope = document) {
    scope.querySelectorAll('[data-quantity-control]').forEach((control) => {
      if (control.dataset.slReady) return;
      control.dataset.slReady = 'true';
      control.addEventListener('click', () => {
        const container = control.closest('[data-quantity], .quantity, .quantity-selector');
        const input = container?.querySelector('input[type="number"]');
        if (!input) return;
        const step = Number(input.step) || 1;
        const current = Number(input.value) || 0;
        const minimum = Number(input.min) || 0;
        const maximum = input.max ? Number(input.max) : Infinity;
        const direction = control.dataset.quantityControl === 'minus' || control.name === 'minus' ? -1 : 1;
        input.value = Math.min(maximum, Math.max(minimum, current + (step * direction)));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  function initStickyProductBar(scope = document) {
    const form = scope.querySelector('[data-main-product-form]') || document.querySelector('[data-main-product-form]');
    const bar = scope.querySelector('[data-sticky-add-to-cart]') || document.querySelector('[data-sticky-add-to-cart]');
    if (!form || !bar || bar.dataset.slReady) return;
    bar.dataset.slReady = 'true';
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => {
      bar.classList.toggle('is-visible', !entry.isIntersecting && window.scrollY > form.offsetTop);
    }, { threshold: 0 });
    observer.observe(form);
  }

  function initPredictiveSearch(scope = document) {
    const forms = new Set(scope.querySelectorAll('[data-predictive-search-form]'));
    scope.querySelectorAll('[data-predictive-search-input]').forEach((input) => {
      if (input.form) forms.add(input.form);
    });
    forms.forEach((form) => {
      const input = form.querySelector('[data-predictive-search-input], input[type="search"]');
      const predictiveScope = form.closest('[data-header-search], [data-search-section]') || form;
      const controlledId = input?.getAttribute('aria-controls');
      const results = (controlledId && document.getElementById(controlledId))
        || predictiveScope.querySelector('[data-predictive-search-results]');
      const status = predictiveScope.querySelector('[data-predictive-search-status]');
      const clearButton = form.querySelector('[data-predictive-search-clear]');
      const loader = form.querySelector('[data-predictive-search-loader]');
      if (!input || !results || input.dataset.slPredictiveReady) return;
      input.dataset.slPredictiveReady = 'true';
      let timer;
      let controller;
      let requestSequence = 0;
      const minimumQueryLength = Math.max(1, Number(input.dataset.minimumQueryLength || form.dataset.minimumQueryLength || 2));

      const resultLinks = () => [...results.querySelectorAll('[role="option"] a, a[role="option"], .predictive-search__all')];
      const announce = (message = '') => {
        if (!status) return;
        status.textContent = '';
        window.requestAnimationFrame(() => { status.textContent = message; });
      };
      const updateClearButton = () => {
        if (clearButton) clearButton.hidden = input.value.length === 0;
      };
      const setLoading = (loading) => {
        form.classList.toggle('is-loading', loading);
        results.toggleAttribute('aria-busy', loading);
        if (loader) loader.hidden = !loading;
        if (!loading) return;

        announce(status?.dataset.loadingText || loader?.textContent.trim() || '');
        if (loader) {
          results.hidden = true;
          input.setAttribute('aria-expanded', 'false');
        } else {
          results.innerHTML = '<div class="predictive-search__loading-indicator" aria-hidden="true">•••</div>';
          results.hidden = false;
          input.setAttribute('aria-expanded', 'true');
        }
      };
      const closeResults = ({ restoreFocus = false } = {}) => {
        results.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        if (restoreFocus) input.focus({ preventScroll: true });
      };
      const resetResults = ({ clearInput = false } = {}) => {
        window.clearTimeout(timer);
        controller?.abort();
        controller = null;
        requestSequence += 1;
        setLoading(false);
        if (clearInput) input.value = '';
        results.innerHTML = '';
        closeResults();
        updateClearButton();
        announce();
      };

      input.addEventListener('input', () => {
        window.clearTimeout(timer);
        controller?.abort();
        controller = null;
        const query = input.value.trim();
        updateClearButton();
        if (query.length < minimumQueryLength) {
          resetResults();
          return;
        }
        timer = window.setTimeout(async () => {
          const currentSequence = ++requestSequence;
          const requestController = new AbortController();
          controller = requestController;
          setLoading(true);
          try {
            const route = input.dataset.predictiveSearchUrl || form.dataset.predictiveSearchUrl || window.steezlabRoutes?.predictiveSearch || `${window.Shopify?.routes?.root || '/'}search/suggest`;
            const sectionId = form.dataset.sectionId || 'predictive-search';
            const url = new URL(route, window.location.origin);
            url.searchParams.set('q', query);
            url.searchParams.set('resources[type]', 'query,product,collection,page,article');
            url.searchParams.set('resources[limit]', '6');
            url.searchParams.set('section_id', sectionId);
            const response = await fetch(url, { signal: requestController.signal, headers: { Accept: 'text/html' } });
            if (!response.ok) throw new Error('Predictive search request failed');
            const html = await response.text();
            if (currentSequence !== requestSequence) return;
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const content = parsed.querySelector(`#shopify-section-${CSS.escape(sectionId)}`) || parsed.body;
            results.innerHTML = content.innerHTML;
            const requestLink = results.querySelector('[data-predictive-request-link]');
            if (requestLink && form.dataset.requestUrl) requestLink.href = form.dataset.requestUrl;
            const isEmptyState = Boolean(results.querySelector('[data-predictive-empty-state]'));
            const hasContent = resultLinks().length > 0 || isEmptyState;
            results.hidden = !hasContent;
            input.setAttribute('aria-expanded', String(hasContent));
            announce(isEmptyState
              ? (status?.dataset.emptyText || 'No results')
              : (status?.dataset.resultsText || 'Search results updated'));
            emit('search', { query });
          } catch (error) {
            if (error.name !== 'AbortError') {
              results.innerHTML = '';
              closeResults();
              announce(status?.dataset.errorText || 'Search is temporarily unavailable');
            }
          } finally {
            if (currentSequence === requestSequence) {
              controller = null;
              setLoading(false);
            }
          }
        }, 180);
      });

      clearButton?.addEventListener('click', () => {
        resetResults({ clearInput: true });
        input.focus({ preventScroll: true });
      });

      form.addEventListener('reset', () => {
        window.setTimeout(() => resetResults(), 0);
      });

      input.addEventListener('focus', () => {
        if (input.value.trim().length >= minimumQueryLength && resultLinks().length > 0) {
          results.hidden = false;
          input.setAttribute('aria-expanded', 'true');
        }
      });

      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && input.getAttribute('aria-expanded') === 'true') {
          event.preventDefault();
          event.stopPropagation();
          closeResults();
          return;
        }
        if (!['ArrowDown', 'ArrowUp'].includes(event.key) || input.getAttribute('aria-expanded') !== 'true') return;
        const links = resultLinks();
        if (!links.length) return;
        event.preventDefault();
        (event.key === 'ArrowDown' ? links[0] : links[links.length - 1]).focus();
      });

      results.addEventListener('keydown', (event) => {
        const current = event.target.closest('a');
        if (!current || !results.contains(current)) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          closeResults({ restoreFocus: true });
          return;
        }

        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        const links = resultLinks();
        const index = links.indexOf(current);
        if (index < 0 || !links.length) return;
        event.preventDefault();

        if (event.key === 'Home') links[0].focus();
        else if (event.key === 'End') links[links.length - 1].focus();
        else if (event.key === 'ArrowDown') links[(index + 1) % links.length].focus();
        else if (index === 0) input.focus({ preventScroll: true });
        else links[index - 1].focus();
      });

      document.addEventListener('pointerdown', (event) => {
        if (!predictiveScope.contains(event.target)) closeResults();
      });

      updateClearButton();
    });
  }

  function initialize(scope = document) {
    initOverlays(scope);
    initReveals(scope);
    initDetails(scope);
    initAnnouncements(scope);
    initWishlist(scope);
    initLocalization(scope);
    initStickyProductBar(scope);
    initPredictiveSearch(scope);
  }

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const openTargets = Array.from(overlays.keys());
    if (openTargets.length) closeOverlay(openTargets[openTargets.length - 1]);
    document.querySelectorAll('details[open][data-close-on-escape], details[open][data-nav-dropdown]').forEach((details) => { details.open = false; });
  });

  document.addEventListener('click', (event) => {
    document.querySelectorAll('details[open][data-close-on-outside], details[open][data-nav-dropdown]').forEach((details) => {
      if (!details.contains(event.target)) details.open = false;
    });
  });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyLocalization(event.target));
  document.addEventListener('shopify:section:select', (event) => initReveals(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  window.Steezlab = {
    initialize,
    emit,
    openOverlay,
    closeOverlay
  };
})();
